import Joi from 'joi';
import moment from 'moment';
import { dataTypes, conditionTypeOperators } from './constants';

const conditionValueValidator = Joi.when('conditionDataType', {
  is: dataTypes.STRING,
  then: Joi.array().items(
    Joi.string()
      .label('Condition')
      .required()
  )
})
  .when('conditionDataType', {
    is: dataTypes.INTEGER,
    then: Joi.array().items(
      Joi.number()
        .integer()
        .label('Condition')
        .required()
    )
  })
  .when('conditionDataType', {
    is: dataTypes.DOUBLE,
    then: Joi.array().items(
      Joi.number()
        .precision(2)
        .label('Condition')
        .required()
    )
  })
  .when('conditionDataType', {
    is: dataTypes.BIGDECIMAL,
    then: Joi.array().items(
      Joi.number()
        .precision(2)
        .label('Condition')
        .required()
    )
  })
  .when('conditionDataType', {
    is: dataTypes.DATE_TIME,
    then: Joi.array().items(
      Joi.date()
        .label('Condition')
        .required()
    )
  })
  .when('conditionDataType', {
    is: dataTypes.DATE,
    then: Joi.array()
      .length(1)
      .custom((value, helpers) => {
        const { dateComparisonOptions } = helpers.state.ancestors[0];
        if (!Array.isArray(dateComparisonOptions) || dateComparisonOptions.length !== 1) {
          return helpers.error('any.invalid', { message: 'Conditions must be an array of length 1' });
        }
        const option = dateComparisonOptions[0];
        if (option.dynamicDate === false) {
          // staticDateField must be a valid date
          if (!option.staticDateField || !moment(option.staticDateField, 'YYYY-MM-DD', true).isValid()) {
            return helpers.error('any.invalid', { message: 'Custom date must be a valid date' });
          }
        } else if (option.dynamicDate === true) {
          // dynamicField must be a non-null string
          if (typeof option.dynamicField !== 'string' || option.dynamicField === null || option.dynamicField === '') {
            return helpers.error('any.invalid', { message: 'Date option must be a non-null string' });
          }
        } else {
          return helpers.error('any.invalid', { message: 'Dynamic Date must be true or false' });
        }
        return value;
      })
      .label('Date Condition')
      .messages({
        'any.invalid': '{{#message}}',
        'array.length': 'Date value is required'
      })
  })
  .when('conditionDataType', {
    is: dataTypes.BOOLEAN,
    then: Joi.array().items(
      Joi.string()
        .valid('true', 'false')
        .label('Condition')
        .required()
        .messages({
          'any.invalid': 'Condition must be Boolean, either true or false'
        })
    )
  });

const conditionLessThanValueValidator = Joi.when('conditionDataType', {
  is: dataTypes.INTEGER,
  then: Joi.array().items(
    Joi.number()
      .integer()
      .positive()
      .label('Condition')
      .required()
  )
})
  .when('conditionDataType', {
    is: dataTypes.DOUBLE,
    then: Joi.array().items(
      Joi.number()
        .precision(2)
        .label('Condition')
        .required()
    )
  })
  .when('conditionDataType', {
    is: dataTypes.BIGDECIMAL,
    then: Joi.array().items(
      Joi.number()
        .precision(2)
        .label('Condition')
        .required()
    )
  })
  .when('conditionDataType', {
    is: dataTypes.DATE_TIME,
    then: Joi.array().items(
      Joi.date()
        .label('Condition')
        .required()
    )
  })
  .when('conditionDataType', {
    is: dataTypes.DATE,
    then: Joi.array()
      .length(1)
      .custom((value, helpers) => {
        const { dateComparisonOptions } = helpers.state.ancestors[0];
        if (!Array.isArray(dateComparisonOptions) || dateComparisonOptions.length !== 1) {
          return helpers.error('any.invalid', { message: 'Conditions must be an array of length 1' });
        }
        const option = dateComparisonOptions[0];
        if (option.dynamicDate === false) {
          // staticDateField must be a valid date
          if (!option.staticDateField || !moment(option.staticDateField, 'YYYY-MM-DD', true).isValid()) {
            return helpers.error('any.invalid', { message: 'Custom date must be a valid date' });
          }
        } else if (option.dynamicDate === true) {
          // dynamicField must be a non-null string
          if (typeof option.dynamicField !== 'string' || option.dynamicField === null || option.dynamicField === '') {
            return helpers.error('any.invalid', { message: 'Date option must be a non-null string' });
          }
        } else {
          return helpers.error('any.invalid', { message: 'Dynamic Date must be true or false' });
        }
        return value;
      })
      .label('Date Condition')
      .messages({
        'any.invalid': '{{#message}}',
        'array.length': 'Date value is required'
      })
  });

const minMaxconditionValueValidator = Joi.when('conditionDataType', {
  is: dataTypes.STRING,
  then: Joi.array().items(
    Joi.string()
      .label('Condition')
      .required()
  )
})
  .when('conditionDataType', {
    is: dataTypes.INTEGER,
    then: Joi.array()
      .label('Min and Max')
      .ordered(
        Joi.number()
          .integer()
          .label('Condition')
          .required(),
        Joi.number()
          .integer()
          .greater(Joi.ref('0'))
          .label('Condition')
          .required()
          .messages({ 'number.greater': 'Max must be greater than min' })
      )
      .messages({ 'array.includesRequiredKnowns': 'Min and Max are required' })
  })
  .when('conditionDataType', {
    is: dataTypes.DOUBLE,
    then: Joi.array()
      .label('Min and Max')
      .ordered(
        Joi.number()
          .precision(2)
          .label('Condition')
          .required(),
        Joi.number()
          .precision(2)
          .greater(Joi.ref('0'))
          .label('Condition')
          .required()
          .messages({ 'number.greater': 'Max must be greater than min' })
      )
      .messages({ 'array.includesRequiredKnowns': 'Min and Max are required' })
  })
  .when('conditionDataType', {
    is: dataTypes.DATE,
    then: Joi.array()
      .length(2)
      .custom((value, helpers) => {
        const { dateComparisonOptions } = helpers.state.ancestors[0];
        if (!Array.isArray(dateComparisonOptions) || dateComparisonOptions.length !== 2) {
          return helpers.error('any.invalid', { message: 'Conditions must be an array of length 2' });
        }
        // Check dateFormatType consistency
        if (dateComparisonOptions[0].dateFormatType !== dateComparisonOptions[1].dateFormatType) {
          return helpers.error('any.invalid', { message: 'Both date conditions must have the same dateFormatType' });
        }
        let bothStatic = true;
        for (let i = 0; i < 2; i++) {
          const option = dateComparisonOptions[i];
          if (option.dynamicDate === false) {
            // staticDateField must be a valid date
            if (!option.staticDateField || !moment(option.staticDateField, 'YYYY-MM-DD', true).isValid()) {
              return helpers.error('any.invalid', { message: `Custom date at index ${i} must be a valid date` });
            }
          } else if (option.dynamicDate === true) {
            bothStatic = false;
            // dynamicField must be a non-null string
            if (typeof option.dynamicField !== 'string' || option.dynamicField === null || option.dynamicField === '') {
              return helpers.error('any.invalid', { message: `Date option at index ${i} must be a non-null string` });
            }
          } else {
            return helpers.error('any.invalid', { message: `Dynamic Date at index ${i} must be true or false` });
          }
        }
        // If both are static, check that value[1] > value[0]
        if (bothStatic) {
          if (moment(value[1], "YYYY-MM-DD").toDate() <= moment(value[0], "YYYY-MM-DD").toDate()) {
            return helpers.error('date.greater', { limit: value[0] });
          }
        }
        return value;
      })
      .label('Min and Max')
      .messages({
        'any.invalid': '{{#message}}',
        'array.length': 'Min and Max are required',
        'date.greater': 'Minimum date must be less than maximum date'
      })
  })
  .when('conditionDataType', {
    is: dataTypes.DATE_TIME,
    then: Joi.array()
      .label('Min and Max')
      .ordered(
        Joi.date()
          .label('Condition')
          .required(),
        Joi.date()
          .greater(Joi.ref('0'))
          .label('Condition')
          .required()
          .messages({ 'date.greater': 'Max must be greater than min' })
      )
      .messages({ 'array.includesRequiredKnowns': 'Min and Max are required' })
  });

const queryParamsValidator = Joi.object({
  conditionAttribute: Joi.string()
    .label('Reservation Property')
    .required(),
  condition: Joi.string()
    .label('Operator')
    .required(),

  conditionValue: Joi.when('condition', {
    is: conditionTypeOperators.LESS_THAN,
    then: conditionLessThanValueValidator
  })
    .when('condition', {
      is: conditionTypeOperators.LESS_THAN_OR_EQUALS,
      then: conditionLessThanValueValidator
    })
    .when('condition', {
      is: conditionTypeOperators.GREATER_THAN,
      then: conditionValueValidator
    })
    .when('condition', {
      is: conditionTypeOperators.GREATER_THAN_OR_EQUALS,
      then: conditionValueValidator
    })
    .when('condition', {
      is: conditionTypeOperators.EQUALS,
      then: conditionValueValidator
    })
    .when('condition', {
      is: conditionTypeOperators.DOES_NOT_EQUAL,
      then: conditionValueValidator
    })
    .when('condition', {
      is: conditionTypeOperators.CONTAINS,
      then: conditionValueValidator
    })
    .when('condition', {
      is: conditionTypeOperators.DOES_NOT_CONTAIN,
      then: conditionValueValidator
    })
    .when('condition', {
      is: conditionTypeOperators.MATCH_REG_EX,
      then: conditionValueValidator
    })
    .when('condition', {
      is: conditionTypeOperators.RANGE,
      then: minMaxconditionValueValidator
    })
});

export { queryParamsValidator };
